import { isNull } from 'lodash/fp';
import React, { useCallback, useMemo, useState } from 'react';
import { Options } from 'react-select';

import { useAppSelector } from '../../../hooks/react-redux';
import { getIsEmptyConfig, riskAssociatedColors, riskAssociatedLabel, riskLevels } from '../../admin/risk-tolerance/utils';
import { fieldListIcons } from '../../admin/dataset-builder/FieldList';
import { getAllDropdownLists } from '../../admin/dropdown-lists/store';
import { CurrencyRiskConfig, RiskAssociatedValue as RiskAssociatedValueType, RiskField, RiskAssociated } from '../../admin/risk-tolerance/store';
import { DatasetFieldType } from '../../datasets/store';
import { Dropdown, DropdownOption } from '../dropdown/Dropdown';
import { Icon } from '../icon/Icon';
import { Scrollable } from '../scrollable/Scrollable';
import { InformationTooltip, OverflowTooltip } from '../tooltip';
import { ConfiguredCurrenciesModal } from './ConfiguredCurrenciesModal';
import { RiskAssociatedValue } from './RiskAssociatedValue';
import styles from './RiskTolerance.module.scss';
import { RiskToleranceSlider } from './Slider';
import { RadioButton } from '../button/RadioButton';
import { Tick } from '../icons';

const { frenchSelected } = styles;

interface RiskToleranceConfigProps {
    riskField: RiskField;
    setWeighting: (weighting: number) => void;
    setFallbackPosition: (fallbackPosition: number) => void;
    updateRiskConfig: (riskAssociated: RiskAssociated, value: RiskAssociatedValueType, selectedCurrency?: string) => void;
    showRiskFieldToggles: boolean;
    toggleRiskField: () => void;
}

export const RiskToleranceConfig: React.FC<RiskToleranceConfigProps> = ({ riskField, setWeighting, updateRiskConfig, showRiskFieldToggles, toggleRiskField, setFallbackPosition }) => {
    const { label, type, weighting, includeRiskField, riskConfig, fallbackRiskPosition } = riskField;
    const riskAssociatedValues = Object.values(RiskAssociated).filter(risk => typeof risk !== 'string') as RiskAssociated[];
    const title = useMemo(() => !includeRiskField ? `${label} (NOT INCLUDED IN RISK TOLERANCE)` : label, [label, includeRiskField]);

    const [selectedCurrency, setSelectedCurrency] = useState<DropdownOption | null>(null);
    const [modalOpen, setModalOpen] = useState<boolean>(false);
    const dropdownLists = useAppSelector(getAllDropdownLists);

    const updateDropdownOption = (option: DropdownOption | Options<DropdownOption> | null) => {
        let newValue = null;
        if (!isNull(option)) {
            newValue = (option as DropdownOption).value;
            setSelectedCurrency({ label: newValue, value: newValue });
        } else {
            setSelectedCurrency(null);
        }
    };

    const showCurrencyDropdown = useMemo(() => type === DatasetFieldType.CURRENCY_AMOUNT, [type]);
    const currencyNotSelected = useMemo(() => showCurrencyDropdown && isNull(selectedCurrency), [showCurrencyDropdown, selectedCurrency]);
    const currency = useMemo(() => isNull(selectedCurrency) ? undefined : selectedCurrency.label, [selectedCurrency]);
    const currenciesWithConfig = useMemo(() => showCurrencyDropdown ? Object.keys(riskConfig as CurrencyRiskConfig) : [], [showCurrencyDropdown, riskConfig]);
    const hasConfiguredCurrencies = useMemo(() => currenciesWithConfig.length > 0, [currenciesWithConfig]);
    const currencyBackground = useCallback((option: string) => currenciesWithConfig.includes(option) ? frenchSelected : undefined, [currenciesWithConfig]);

    const isEmptyConfig = useMemo(() => getIsEmptyConfig(riskField, currency), [riskField, currency]);

    const currencyCodeOptions: DropdownOption[] = useMemo(() => {
        const currencyCodes = dropdownLists.find(({ name }) => name === 'Currency Codes');
        return currencyCodes ? currencyCodes.options.map(value => ({ value, label: value, highlightColour: currencyBackground(value) })) : [];
    }, [dropdownLists, currencyBackground]);

    const openModal = useCallback(() => setModalOpen(true), []);
    const closeModal = useCallback(() => setModalOpen(false), []);

    const titleWidth = useMemo(() => `calc(100% - ${showCurrencyDropdown ? '500' : '40'}px)`, [showCurrencyDropdown]);

    const fallbackMarks = useMemo(() => riskLevels.map(riskAssociated => ({ key: riskAssociated, label: riskAssociatedLabel[riskAssociated] })), []);

    return (
        <>
            <div className={styles.configHeader}>
                <div className={styles.configTitle} style={{ width: titleWidth }}><OverflowTooltip overlayText={title} /></div>
                {showCurrencyDropdown && <div className={styles.currencyWrapper}>
                    {hasConfiguredCurrencies && <div className={styles.configuredCurrenciesButton} onClick={openModal}>Configured Currencies</div>}
                    <InformationTooltip content='Configured currencies will be highlighted in the dropdown' placement='top' />
                    <div className={styles.currencySelect}>
                        <Dropdown
                            onChange={updateDropdownOption}
                            value={selectedCurrency}
                            options={currencyCodeOptions}
                        />
                    </div>
                </div>}
                <div className={styles.configType}><Icon icon={fieldListIcons[type]} fontSize={40} /></div>
            </div>
            <div className={styles.configBody}>
                <div className={styles.configRiskWrapper}>
                    <div className={styles.configRiskHeader}>Risk Associated</div>
                    <div className={styles.riskConfiguration}>
                        <Scrollable>
                            {riskAssociatedValues.map(risk => (
                                <div className={styles.riskAssociatedOption} key={risk}>
                                    <div className={styles.riskAssociatedLabel} style={{ backgroundColor: riskAssociatedColors[risk] }}>{riskAssociatedLabel[risk]}</div>
                                    <RiskAssociatedValue riskField={riskField} riskAssociated={risk} updateRiskConfig={updateRiskConfig} selectedCurrency={currency} />
                                </div>
                            ))}
                        </Scrollable>
                    </div>
                </div>
                <div className={styles.rightConfigWrapper}>
                    <div className={styles.configuration} style={{ height: `${showRiskFieldToggles ? 80 : 100}%` }}>
                        <div className={styles.configHeader}>
                            <div className={styles.configWeightingHeader}><OverflowTooltip overlayText='Level of Importance' /></div>
                            <div className={styles.configFallbackHeader}><OverflowTooltip overlayText='Default Position' /></div>
                        </div>
                        <div className={styles.configContentWrapper}>
                            <div className={styles.configWeightingContent}>
                                <RiskToleranceSlider
                                    value={weighting}
                                    setValue={setWeighting}
                                    disabled={!includeRiskField}
                                    shouldAdjustValueForRisk
                                />
                            </div>
                            <div className={styles.configFallbackContent}>
                                <RiskToleranceSlider
                                    value={fallbackRiskPosition}
                                    setValue={setFallbackPosition}
                                    disabled={isEmptyConfig}
                                    marks={fallbackMarks}
                                    renderLeft
                                />
                            </div>
                        </div>
                    </div>
                    {showRiskFieldToggles &&
                        <div className={styles.includeFieldWrapper}>
                            <div className={styles.includeFieldLabel}>I want to exclude this field from my Risk Tolerance configuration</div>
                            <RadioButton fontSize={10} withBackground={!includeRiskField} onClick={toggleRiskField} icon={Tick} />
                        </div>
                    }
                </div>
                {currencyNotSelected && <div className={styles.currencyOverlay} />}
                {showCurrencyDropdown && <ConfiguredCurrenciesModal isOpen={modalOpen} closeModal={closeModal} riskConfig={riskConfig as CurrencyRiskConfig} setSelectedCurrency={setSelectedCurrency} />}
            </div>
        </>
    );
};
